import { useEffect, useRef } from 'react';
import { Button, type ButtonVariant } from '../Button';
import { createFocusTrap } from '../../utils/focusTrap';
import './ConfirmDialog.css';

export interface ConfirmDialogButton {
  label: string;
  onClick: () => void;
  /**
   * Brand button variant. Used when `className` is not provided.
   * Defaults to `'outline'`.
   */
  variant?: ButtonVariant;
  /**
   * Escape hatch: when provided, the button is rendered as a plain
   * `<button>` with this className (no brand styling applied). Use this
   * when the consumer app already has its own button class system.
   */
  className?: string;
}

export interface ConfirmDialogCheckboxProps {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: string;
  buttons: ConfirmDialogButton[];
  checkbox?: ConfirmDialogCheckboxProps;
  /**
   * Dismiss the dialog without acting — bound to **Escape**.
   *
   * `createFocusTrap` also watches for Escape, but only forwards it to a button
   * whose `aria-label` contains "Close"; a confirm dialog has action buttons, so
   * Escape did nothing here. Supply this and Escape becomes the safe way out.
   *
   * Deliberately opt-in rather than inferred from the buttons: guessing which
   * button "means cancel" would eventually guess wrong, and on a dialog whose
   * whole purpose is guarding a destructive action, wrong means destroying
   * something the user was trying to back out of. Escape must never confirm.
   */
  onDismiss?: () => void;
}

export function ConfirmDialog({
  open,
  title,
  message,
  buttons,
  checkbox,
  onDismiss,
}: ConfirmDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open && dialogRef.current) {
      const cleanup = createFocusTrap(dialogRef.current);
      return cleanup;
    }
    return undefined;
  }, [open]);

  // Escape closes. Listening on the document rather than the dialog node keeps
  // this working while focus sits on the overlay or is momentarily on <body>
  // (the trap moves focus in on open, but a consumer may move it again).
  useEffect(() => {
    if (!open || !onDismiss) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation(); // don't also trigger an app-level Escape handler
      onDismiss();
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [open, onDismiss]);

  if (!open) return null;

  return (
    <div className="confirm-dialog-overlay">
      <div
        ref={dialogRef}
        className="confirm-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-message"
      >
        <h3 id="confirm-dialog-title" className="confirm-dialog-title">
          {title}
        </h3>
        <p id="confirm-dialog-message" className="confirm-dialog-message">
          {message}
        </p>
        {checkbox !== undefined && (
          <label className="confirm-dialog-checkbox">
            <input
              type="checkbox"
              checked={checkbox.checked}
              onChange={(e) => checkbox.onChange(e.target.checked)}
            />
            <span>{checkbox.label}</span>
          </label>
        )}
        <div className="confirm-dialog-buttons">
          {buttons.map((btn, i) =>
            btn.className ? (
              <button
                key={i}
                type="button"
                onClick={btn.onClick}
                className={btn.className}
              >
                {btn.label}
              </button>
            ) : (
              <Button
                key={i}
                variant={btn.variant ?? 'outline'}
                onClick={btn.onClick}
              >
                {btn.label}
              </Button>
            ),
          )}
        </div>
      </div>
    </div>
  );
}
